import { tWithKeyAttr as t } from "../../i18nHelper";
import React, { useEffect, useState, useMemo, ChangeEvent } from "react";
import BackArrowButton from "../../components/backArrowButton/backArrowButton";
import { useNavigate } from "react-router-dom";
import styles from "../treatmentRooms/treatmentRooms.module.css";
import useMediaQuery from "../../hooks/useMediaQuery";
import { useDebounce } from "../../hooks/useDebounce";
import { filterObjectsByString } from "../../utils/search";
import Loader from "../../components/loader/loader";
import Modal from "../../components/modals/modal/Modal";
import DeleteConfirmation from "../../components/modals/deleteConfirmation/deleteConfirmation";
import GeneralSuccessPopup from "../../components/modals/generalSuccessPopup/generalSuccessPopup";
import ErrorPopup from "../../components/modals/errorPopup/errorPopup";
import TreatmentGroupsList from "../../components/treatmentGroupsList/treatmentGroupsList";
import AddOrEditTreatmentGroupForm from "../../components/forms/addOrEditTreatmentGroup/addOrEditTreatmentGroup";
import AddTreatmentPlan from "../../components/addTreatmentPlan/addTreatmentPlan";
import GroupTreatmentPlans from "../../components/groupTreatmentPlans/groupTreatmentPlans";
import { ITreatmentGroup } from "../../services/types/utils.types";
import { IUserReport } from "../../services/types/users.types";
import { initialTreatmentGroup } from "../../utils/constants";
import { useAppSelector } from "../../services/hooks";
import { ActionMeta, SingleValue } from "react-select";
import { TLabeledItem } from "../../types/types";
import { adminSelector } from "../../services/selectors/user.selectors";
import {
  useGetTreatmentGroupsQuery,
  useDeleteTreatmentGroupMutation as useDeleteGroupMutation,
  useGetTreatmentGroupByIdQuery,
} from "../../services/api/utils.api";
import ExportTableButtons from "../../components/exportTableButtons/ExportTableButtons";

function TreatmentGroupsPage() {
  const navigate = useNavigate();
  const mobile = useMediaQuery("(max-width: 800px)");
  
  const admin = useAppSelector(adminSelector);
  const [deleteGroup] = useDeleteGroupMutation();
  const { data: groupsData, isFetching } = useGetTreatmentGroupsQuery(
    { companyId: admin.companyId },
    { skip: !admin.companyId }
  );

  const groups = groupsData ?? [];
  
  const [inputValue, setInputValue] = useState<string>("");
  const debouncedInputValue = useDebounce(inputValue);
  const filteredGroups = useMemo(() => (
    filterObjectsByString(debouncedInputValue, groups) as ITreatmentGroup[]
  ), [debouncedInputValue, groups]);
  const [groupToEditOrAdd, setGroupToEditOrAdd] = useState<ITreatmentGroup>(initialTreatmentGroup);
  const [showAddOrEditForm, setShowAddOrEditForm] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState<number | undefined>(undefined);
  const [openModalSuccess, setOpenModalSuccess] = useState(false);
  const [openModalError, setOpenModalError] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<ITreatmentGroup | undefined>(undefined);
  const [openModal, setOpenModal] = useState(false);
  const [showTreatmentPlanForm, setShowTreatmentPlanForm] = useState(false);
  const [showTreatmentPlansView, setShowTreatmentPlansView] = useState(false);
  const [groupForTreatmentPlan, setGroupForTreatmentPlan] = useState<ITreatmentGroup | undefined>(undefined);

  // Fetch full group details when editing (use a dedicated id to avoid race)
  const { data: groupDetails } = useGetTreatmentGroupByIdQuery(
    { id: editingGroupId ?? 0 },
    { skip: !editingGroupId || !showAddOrEditForm }
  );

  // Update form with full details including members when data arrives
  useEffect(() => {
    if (groupDetails && showAddOrEditForm) {
      setGroupToEditOrAdd(groupDetails);
    }
  }, [groupDetails, showAddOrEditForm]);

  const [limits, setLimits] = useState({ start: 1, end: 7 });
  const paginatedGroups = filteredGroups.slice(limits.start - 1, limits.end);

  useEffect(() => {
    setLimits({ start: 1, end: 7 });
  }, [debouncedInputValue, groups.length]);

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleDelete = async () => {
    if (!groupToDelete) return;
    try {
      await deleteGroup({ id: groupToDelete.id }).unwrap();
      setOpenModalSuccess(true);
    } catch {
      setOpenModalError(true);
    } finally {
      setGroupToDelete(undefined);
    }
  };

  const openAddOrEditGroupForm = (group?: ITreatmentGroup) => {
    setGroupToEditOrAdd(group ?? initialTreatmentGroup);
    // clear editing id when opening for creation
    setEditingGroupId(undefined);
    setShowAddOrEditForm(true);
  };

  const handleEdit = (group: ITreatmentGroup) => {
    // set the immediate group state (shallow) so the form can render quickly,
    // then set `editingGroupId` so the query fetches full details and updates it.
    setGroupToEditOrAdd(JSON.parse(JSON.stringify(group)));
    setEditingGroupId(group.id);
    setShowAddOrEditForm(true);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setGroupToEditOrAdd((prev) => ({
      ...prev,
      [name]: name === "maxParticipants" ? (value === "" ? undefined : parseInt(value) || 0) : value,
    }));
  };

  const handleSelectChange = (
    newValue: SingleValue<TLabeledItem>,
    { name }: ActionMeta<TLabeledItem>
  ) => {
    if (name && newValue) {
      setGroupToEditOrAdd((prev) => ({
        ...prev,
        [name]: +newValue.value,
      }));
    }
  };

  if (showTreatmentPlanForm && groupForTreatmentPlan) {
    return (
      <div className={styles.formContainer} style={{ width: '95%' }}>
        <AddTreatmentPlan
          onClose={() => {
            setShowTreatmentPlanForm(false);
            // Go back to treatment plans view
            setShowTreatmentPlansView(true);
          }}
          user={{
            Users: {
              Id: 0,
              Guid: "GROUP_" + groupForTreatmentPlan.id,
              CompanyId: admin.companyId,
              FullName: groupForTreatmentPlan.groupName,
              RoleId: 1,
            },
            AvailabilityHours: null,
            AppointmentsProgramsNum: null,
            TotalReports: 0,
            Recommends: null,
            RoleName: null,
          } as IUserReport}
          program={undefined}
          treatmentGroupId={groupForTreatmentPlan.id}
        />
      </div>
    );
  }

  if (showTreatmentPlansView && groupForTreatmentPlan) {
    return (
      <GroupTreatmentPlans
        group={groupForTreatmentPlan}
        onClose={() => {
          setShowTreatmentPlansView(false);
          setGroupForTreatmentPlan(undefined);
        }}
        onAddNew={() => {
          setShowTreatmentPlansView(false);
          setShowTreatmentPlanForm(true);
        }}
      />
    );
  }

  if (showAddOrEditForm) {
    return (
      <div className={styles.formContainer}>
        <AddOrEditTreatmentGroupForm
          form={groupToEditOrAdd}
          handleChange={handleChange}
          handleSelectChange={handleSelectChange}
          onClose={() => {
            setShowAddOrEditForm(false);
            setGroupToEditOrAdd(initialTreatmentGroup);
              setEditingGroupId(undefined);
          }}
        />
      </div>
    );
  }

  return (
    <section className={`section ${styles.section}`}>
      <BackArrowButton
        text={t("PREVIOUS_SCREEN")}
        onClick={() => navigate(-1)}
        position={"center"}
      />
      <h1 className="title title_blue">{t('ALL_TREATMENT_GROUPS')}</h1>
      <div className="header">
        <button
          type={"button"}
          className="addBtn"
          onClick={() => openAddOrEditGroupForm()}
        >
          {!mobile && t('ADD_NEW_TREATMENT_GROUP')}
          <span className="plus">+</span>
        </button>
        <input
          type="text"
          placeholder={t('FILTER_PLACEHOLDER')}
          className="searchInput"
          onChange={onInputChange}
          value={inputValue}
        />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-start', width: '97%', marginTop: '10px', marginBottom: '10px' }}>
        <ExportTableButtons
          entity="TreatmentGroups"
          dataRows={filteredGroups.map(g => ({
            Id: g.id,
            GroupName: g.groupName,
            MaxParticipants: g.maxParticipants !== undefined && g.maxParticipants !== null ? g.maxParticipants : t("UNLIMITED"),
            TherapyType: g.therapyTypeName ?? "-",
            Status: g.statusName ?? (g.statusId === 1 ? t("STATUS_ACTIVE") : t("STATUS_INACTIVE")),
          }))}
        />
      </div>
      {isFetching ? (
        <Loader />
      ) : filteredGroups.length > 0 ? (
        <TreatmentGroupsList
          groups={paginatedGroups}
          total={filteredGroups.length}
          limits={limits}
          setLimits={setLimits}
          onEdit={handleEdit}
          onDeleteClick={(group) => {
            setGroupToDelete(group);
            setOpenModal(true);
          }}
          onOpenTreatmentPlan={(group) => {
            setGroupForTreatmentPlan(group);
            setShowTreatmentPlansView(true);
          }}
        />
      ) : (
        <p className="text">{t("NO_RESULTS_FOUND")}</p>
      )}
      {openModal && (
        <Modal onClose={() => setOpenModal(false)}>
          <DeleteConfirmation
            onClose={() => setOpenModal(false)}
            onDelete={handleDelete}
            text={`${t('DELETE_TREATMENT_GROUP')}: ${groupToDelete?.groupName}?`}
          />
        </Modal>
      )}
      {openModalSuccess && (
        <Modal onClose={() => setOpenModalSuccess(false)}>
          <GeneralSuccessPopup onClose={() => setOpenModalSuccess(false)} />
        </Modal>
      )}
      {openModalError && (
        <Modal onClose={() => setOpenModalError(false)}>
          <ErrorPopup onClose={() => setOpenModalError(false)} />
        </Modal>
      )}
    </section>
  );
}

export default TreatmentGroupsPage;
